import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { CoreContext } from 'context/CoreContext'
import { useAuthLayout } from 'context/AuthLayoutContext'
import { Read } from 'services/core'
import { adapterServiceSupport } from 'utils/adapters'
import { apiRequest, exposeStrapiError, normalizeStrapiRegister } from 'utils'
import { languages } from 'utils/options'
import { isSupportVideosCategory } from 'utils/supportVideos'

export default function useController() {
  const { categoryId, contentId } = useParams()
  const { language } = useContext(CoreContext)
  const { setLayoutProps } = useAuthLayout()
  const locale = useMemo(() => languages.find(item => item.id === language)?.ref || 'en', [language])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [video, setVideo] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(false)

    try {
      const query = `/${contentId}?locale=${encodeURIComponent(locale)}&populate[image]=true&populate[document]=true&populate[tags]=true&populate[categories][populate][title_translations]=true`
      const result = await apiRequest(() => Read('service-supports', query))

      if (exposeStrapiError(result)) {
        setVideo(null)
        setError(true)
        return
      }

      const normalized = normalizeStrapiRegister(result) || result?.data || null
      const adapted = normalized ? adapterServiceSupport(normalized, locale) : null
      const category = adapted?.raw_category?.find(item => `${item?.id}` === `${categoryId}`)

      if (!adapted || !isSupportVideosCategory(category)) {
        setVideo(null)
        setError(true)
        return
      }

      setVideo(adapted)
    } catch {
      setVideo(null)
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [categoryId, contentId, locale])

  useEffect(() => {
    setLayoutProps({})
  }, [setLayoutProps])

  useEffect(() => {
    load()
  }, [load])

  return {
    categoryId,
    error,
    loading,
    retry: load,
    video,
  }
}
